import { computed, reactive } from "vue";
import * as API from "@/api/API";
import eventHub from "@/helpers/utility/Events";
import { useWorkspaces } from "@/api/workspaces.store";

import type { Ref } from "vue";
import type {
  Organization,
  OrganizationMember,
  OrganizationRole
} from "@/api/Organization";
import type { User } from "@/api/User";
import type { OrganizationWorkspace } from "@/api/Workspace";

const organizations = reactive({} as Record<number, Organization>);

function findOrganizationBySlug(slug?: string) {
  if (!slug) return null;
  return Object.values(organizations).find((t) => slug == t.slug);
}

function rolesByCode(organization: Organization) {
  const roleArray = organization?.roles || [];
  const roles: Record<string, OrganizationRole> = {};
  for (const role of roleArray) {
    roles[role.roleCode] = role;
  }
  return roles;
}

export function useOrganizations(
  props: { organizationSlug?: string },
  { user }: { user?: Ref<User | null> } = {}
) {
  const { workspaces, updateWorkspaceEntry, teamsByOrganization } =
    useWorkspaces(props);

  const organizationsBySlug = computed(() => {
    const list: Record<string, Organization> = {};
    for (const organizationId in organizations) {
      const organization = organizations[organizationId];
      list[organization.slug] = organization;
    }
    return list;
  });

  const ownedOrganizations = computed(() => {
    return Object.values(organizations).filter((organization) => {
      return organization?.membership?.roleCode === "OWNER";
    });
  });

  const userOrganizations = computed(() => {
    return Object.values(organizations).filter(
      (organization) => organization?.membership != null
    );
  });

  const currentOrganization = computed(() =>
    findOrganizationBySlug(props.organizationSlug)
  );
  const currentOrganizationRoles = computed(() =>
    currentOrganization.value ? rolesByCode(currentOrganization.value) : {}
  );

  const currentOrganizationMembership = computed(() => {
    return currentOrganization.value?.membership;
  });

  const currentOrganizationRole = computed(() => {
    const roles = currentOrganizationRoles.value;
    if (!roles) return null;
    const membership = currentOrganizationMembership.value;
    if (!membership) return null;
    return roles[membership.roleCode];
  });

  const acceptedOrganizationLicenses = computed(() => {
    return Object.values(organizations).filter((organization) => {
      const membership = organization.membership;
      if (!membership.accepted) return false;
      return true;
    });
  });

  const activeOrganizationLicenses = computed(() => {
    return acceptedOrganizationLicenses.value.filter((organization) => {
      const membership = organization.membership;
      if (membership.expired) return false;
      return true;
    });
  });

  const pendingOrganizationLicenses = computed(() => {
    return Object.values(organizations).filter((organization) => {
      const membership = organization.membership;
      if (membership.expired) return false;
      if (!membership.accepted) return true;
      return false;
    });
  });

  function saveOrganization(organization: Organization) {
    for (const ws of organization.teams || []) {
      updateWorkspaceEntry(ws.id, ws);
    }

    organizations[organization.id] = organization;
  }

  function setOrganizationMembersRecords(
    organizationId: number,
    members: OrganizationMember[]
  ) {
    const organization = organizations[organizationId];
    organization.members = members;

    if (user?.value) {
      const userMember = organization.members.find(
        (m) => m.id == user.value?.id
      );
      organization.membership = userMember!;
    }
  }

  function updateOrganizationMemberRecord(
    organizationId: number,
    member: OrganizationMember
  ) {
    // add the updated member to the organization record
    const organizationEntry = organizations[organizationId];
    const members = organizationEntry.members ?? [];
    const memberIndex = members.findIndex((m) => m.id == member.id);
    if (memberIndex > -1) {
      members.splice(memberIndex, 1, member);
    } else {
      members.push(member);
    }

    setOrganizationMembersRecords(organizationId, members);
  }

  // === MEMBERS ===

  async function getOrganizationMembers(organizationData: {
    id: number;
    slug: string;
  }) {
    const organizationId = organizationData.id;
    const organizationSlug = organizationData.slug;
    const members = await API.get(
      `/v1/organizations/${organizationSlug}/members`
    );

    setOrganizationMembersRecords(organizationId, members);

    return members;
  }

  async function getOrganizationMember(
    organization: { slug: string },
    userId: number
  ) {
    const organizationSlug = organization.slug;
    return await API.get<OrganizationMember>(
      `/v1/organizations/${organizationSlug}/members/${userId}`
    );
  }

  async function updateOrganizationMember(
    organization: { id: number; slug: string },
    newMemberData: Partial<OrganizationMember>
  ) {
    const organizationSlug = organization.slug;
    const userId = newMemberData.id;
    const updatedMember = await API.patch<OrganizationMember>(
      `/v1/organizations/${organizationSlug}/members/${userId}`,
      {
        data: newMemberData
      }
    );

    if (newMemberData.roleCode === "OWNER") {
      await getOrganizationMembers(organization);
    } else {
      // add the updated member to the organization record
      const organizationEntry = findOrganizationBySlug(organizationSlug);
      if (!organizationEntry) {
        throw new Error("Organization not found.");
      }
      updateOrganizationMemberRecord(organizationEntry.id, updatedMember);
    }

    return updatedMember;
  }

  async function makeOrganizationMemberOwner(
    organization: { slug: string },
    userId: number
  ) {
    const organizationSlug = organization.slug;
    const updatedMember = await API.post<OrganizationMember>(
      `/v1/organizations/${organizationSlug}/members/${userId}/make_owner`
    );

    // add the updated member to the organization record
    const organizationEntry = findOrganizationBySlug(organizationSlug);
    if (!organizationEntry) {
      throw new Error("Organization not found.");
    }
    updateOrganizationMemberRecord(organizationEntry.id, updatedMember);

    return updatedMember;
  }

  /**
   * Send out invitations to new organization members. Each invited member will get a license.
   * @param {Object} organization
   * @param {Number} organization.id
   * @param {String} organization.slug
   * @param {Array} entries
   */
  async function inviteOrganizationMembers(
    organization: {
      id: number;
      slug: string;
    },
    entries: { user: { email: string }; roleCode?: string }[]
  ) {
    const organizationSlug = organization.slug;
    const _members = await API.post<OrganizationMember[]>(
      `/v1/organizations/${organizationSlug}/members`,
      {
        data: entries
      }
    );

    const organizationEntry = organizations[organization.id];
    const members = organizationEntry?.members ?? [
      organizationEntry.membership
    ];
    const updatedMembers = members.concat(_members);
    setOrganizationMembersRecords(organization.id, updatedMembers);

    return _members;
  }

  async function joinOrganization(
    organization: {
      slug: string;
    },
    user: { id: number }
  ) {
    if (organization == null) {
      throw new Error("Choose an organization.");
    }

    const organizationSlug = organization.slug;
    const userId = user.id;
    const updatedMember = await API.post(
      `/v1/organizations/${organizationSlug}/members/${userId}/accept`
    );

    const _organization = findOrganizationBySlug(organizationSlug);
    if (!_organization) {
      throw new Error("Organization not found.");
    }
    updateOrganizationMemberRecord(_organization.id, updatedMember);

    return updatedMember;
  }

  /**
   * Leave a organization the user is a member of
   */
  async function leaveOrganization(
    organization: {
      slug: string;
    }
  ) {
    if (!user?.value) {
      throw new Error("User not found.");
    }

    await removeOrganizationMember(organization, user?.value);

    const organizationEntry = findOrganizationBySlug(organization.slug);
    if (organizationEntry) {
      delete organizations[organizationEntry.id];
    }

    // remove the organization from the user's licenses
    if (user.value.licenses) {
      const licenseIndex = user.value.licenses.findIndex(
        (l) => l.providedBy?.slug === organization.slug
      );
      if (licenseIndex > -1) {
        user.value.licenses.splice(licenseIndex, 1);
      }
    }
  }

  /**
   * Remove a member from the organization; can be used to leave a organization.
   */
  const removeOrganizationMember = async (
    organization: {
      slug: string;
    },
    userToRemove: {
      id: number;
    }
  ) => {
    const organizationSlug = organization.slug;
    const userId = userToRemove.id;
    const body = await API.destroy(
      `/v1/organizations/${organizationSlug}/members/${userId}`
    );

    // remove the user from the internal organization members cache
    const _organization = findOrganizationBySlug(organizationSlug);
    if (_organization) {
      const members = _organization.members ?? [];
      const memberIndex = members.findIndex((m) => m.id == userId);
      if (memberIndex > -1) {
        members.splice(memberIndex, 1);
      }

      // remove the organization entry too if the user removed themself.
      if (userId === user?.value?.id) {
        delete organizations[_organization.id];
      }
    }

    return body;
  };

  return {
    organizations,
    userOrganizations,
    organizationsBySlug,
    ownedOrganizations,
    currentOrganization,
    currentOrganizationRole,
    currentOrganizationRoles,
    currentOrganizationMembership,

    activeOrganizationLicenses,
    acceptedOrganizationLicenses,
    pendingOrganizationLicenses,

    ROLE_CODES: {
      MEMBER: "MEMBER",
      ADMIN: "ADMIN",
      OWNER: "OWNER"
    },

    getOrganizationMembers,
    getOrganizationMember,
    updateOrganizationMember,
    makeOrganizationMemberOwner,
    inviteOrganizationMembers,
    joinOrganization,
    leaveOrganization,
    removeOrganizationMember,

    /**
     * Retrieve all organizations the user is in.
     * (and cache the records)
     */
    async getOrganizations(requestOptions = {}) {
      const _organizations = await API.get<Organization[]>(
        "/v1/organizations",
        requestOptions
      );

      for (const organization of _organizations) {
        saveOrganization(organization);
      }

      return _organizations;
    },

    /**
     * Retrieve a organization the user is in.
     * (and cache the record)
     */
    async getOrganization(organizationSlug: string) {
      try {
        const _organization = await API.get(
          `/v1/organizations/${organizationSlug}`
        );

        saveOrganization(_organization);

        return _organization;
      } catch (e) {
        const error = e as Error & { status?: number };
        // delete the cached data if user has no access anymore:
        if (error.status === 403) {
          const organizationEntry = findOrganizationBySlug(organizationSlug);
          if (organizationEntry) {
            delete organizations[organizationEntry.id];
          }
        }
        throw error;
      }
    },

    async updateOrganization(
      organizationSlug: string,
      organizationData: Partial<Organization>
    ) {
      const organization = await API.patch<Organization>(
        `/v1/organizations/${organizationSlug}`,
        {
          data: organizationData
        }
      );

      saveOrganization(organization);

      return organization;
    },

    async destroyOrganization(organization: { slug: string; id: number }) {
      const organizationSlug = organization.slug;
      const organizationId = organization.id;

      await API.destroy(`/v1/organizations/${organizationSlug}`);

      // remove the organizations teams
      const organizationEntry = findOrganizationBySlug(organizationSlug);
      const teams = organizationEntry?.teams ?? [];

      for (const team of teams) {
        updateWorkspaceEntry(team?.id, null);
      }

      delete organizations[organizationId];
    },

    // === WORKSPACES ===

    /**
     * Returns all the teams of an organization
     * @param {Object} organization
     * @param {String} organization.slug
     */
    async getTeams(organization: { slug: string }) {
      const organizationSlug = organization.slug;
      const teams = await API.get<OrganizationWorkspace[]>(
        `/v1/organizations/${organizationSlug}/teams`
      );

      const organizationEntry = findOrganizationBySlug(organizationSlug);
      if (!organizationEntry) {
        throw new Error("Organization not found.");
      }
      organizationEntry.teams = teams;

      // add/update workspace entries in workspace store
      for (const team of teams) {
        updateWorkspaceEntry(team.id, team);
      }

      return teams;
    },
    /**
     * Returns a single team of an organization
     */
    async getTeam(organization: { slug: string }, teamSlug: string) {
      const organizationSlug = organization.slug;
      const team = await API.get<OrganizationWorkspace>(
        `/v1/organizations/${organizationSlug}/teams/${teamSlug}`
      );

      // add the team entry to the organization record
      const organizationEntry = findOrganizationBySlug(organizationSlug);
      if (organizationEntry) {
        const organizationTeams = organizationEntry?.teams ?? [];
        const wsIndex = organizationTeams.findIndex((w) => w.id == team.id);
        if (wsIndex > -1) organizationTeams[wsIndex] = team;
        else organizationTeams.push(team);
        organizationEntry.teams = organizationTeams;
      }

      // add the team to the workspaces records
      updateWorkspaceEntry(team.id, team);

      return team;
    },
    async createTeam(
      organization: { slug: string },
      workspaceData: {
        name: string;
        members: { email: string; roleCode: string }[];
      }
    ) {
      const organizationSlug = organization.slug;
      const newTeam = await API.post<OrganizationWorkspace>(
        `/v1/organizations/${organizationSlug}/teams`,
        { data: workspaceData }
      );

      // add workspace to organization record
      const organizationEntry = findOrganizationBySlug(organizationSlug);
      if (!organizationEntry) {
        throw new Error("Organization not found.");
      }
      if (!organizationEntry.teams) organizationEntry.teams = [];
      organizationEntry.teams.push(newTeam);

      updateWorkspaceEntry(newTeam.id, newTeam);

      return newTeam;
    },
    async updateTeam(
      organization: { slug: string },
      workspaceData: Partial<OrganizationWorkspace>
    ) {
      const organizationSlug = organization.slug;
      const teamSlug = workspaceData.slug;
      const updatedTeam = await API.patch<OrganizationWorkspace>(
        `/v1/organizations/${organizationSlug}/teams/${teamSlug}`,
        { data: workspaceData }
      );

      updateWorkspaceEntry(updatedTeam.id, updatedTeam);

      return updatedTeam;
    },
    async destroyTeam(team: {
      id: number;
      organizationSlug: string;
      slug: string;
    }) {
      const organizationSlug = team.organizationSlug;
      const teamSlug = team.slug;
      await API.destroy(
        `/v1/organizations/${organizationSlug}/teams/${teamSlug}`
      );

      // remove the workspace from the organization data
      const organizationEntry = findOrganizationBySlug(organizationSlug);
      if (!organizationEntry) {
        throw new Error("Organization not found.");
      }
      const teamsOfOrganization = organizationEntry.teams ?? [];
      const index = teamsOfOrganization.findIndex((w) => w.slug === team.slug);
      if (index > -1) {
        teamsOfOrganization.splice(index, 1);
      }

      if (workspaces[team?.id]) delete workspaces[team?.id];

      return true;
    },

    /**
     *
     * @param {Object} team
     * @param {string} team.slug
     * @param {string} team.organizationSlug
     * @param {number} team.order
     */
    async moveTeam(team: {
      slug: string;
      organizationSlug: string;
      order: number;
    }) {
      const teamSlug = team.slug;
      const organizationSlug = team.organizationSlug;

      const orgTeams = teamsByOrganization.value?.[organizationSlug] ?? [];
      const teamEntry = orgTeams.find((t) => t.slug == teamSlug);
      const oldOrder = teamEntry?.order ?? team.order;

      const updatedTeam = await API.post<OrganizationWorkspace>(
        `/v1/organizations/${organizationSlug}/teams/${teamSlug}/move`,
        {
          data: team
        }
      );

      const newOrder = updatedTeam.order;

      // reorder all teams locally:
      if (orgTeams) {
        if (newOrder > oldOrder) {
          orgTeams
            .filter((t) => t.order > oldOrder && t.order <= newOrder)
            .forEach((t) => (t.order -= 1));
        } else if (newOrder < oldOrder) {
          orgTeams
            .filter((t) => t.order < oldOrder && t.order >= newOrder)
            .forEach((t) => (t.order += 1));
        }
      }

      updateWorkspaceEntry(updatedTeam.id, updatedTeam);
    }
  };
}

function reset() {
  for (const organizationId in organizations) {
    delete organizations[organizationId];
  }
}

eventHub.on("user-changed", (user) => {
  reset();

  const userOrganizations = user?.organizations ?? [];

  for (const organization of userOrganizations) {
    organizations[organization.id] = organization;
  }

  console.log(organizations);
});
